import { AlertModal } from '@src/provider/AlertProvider';
import { LogoIcon, LogOutIcon, SmallLogoIcon } from '@src/svg';
import { encrypt } from '@src/utils';
import { useAppSelector } from '@store/hooks';
import { selectContractor } from '@store/slices/contractors/contractorSlice';
import { selectGlobal } from '@store/slices/global/global';
import { selectMembersInfo } from '@store/slices/members/info/membersInfoSlice';
import { selectPayorsInfo } from '@store/slices/payors/info/payorsInfoSlice';
import { selectPlaces } from '@store/slices/places/placesSlice';
import { selectRides } from '@store/slices/rides/ridesSlice';
import { selectUsers } from '@store/slices/users/usersSlice';
import { sessionSetter, tabNameSetter } from '@utils/storage';
import { FC, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { data, getHomeNavigationData, navigationItemType } from './data';
import { useLogout } from '@hooks/useLogout';
import { X_HEADER_ID_NAME } from '@utils/constants';
import { useGetCanCreateRideStatusQuery } from '@store/rtk/payors';
import { CONTRACTOR_USER } from '@routes/helpers';
import InfoTooltip from '@uikit/Info/InfoTooltip/InfoTooltip';
import TruncatedText from '@component/TruncatedText/TruncatedText';
import NavFlyoutTooltip from './NavFlyoutTooltip';
import { TAB_NAME_TITLES } from '@hooks/useDefinePermissions';

const flyoutSlotProps = {
  popper: {
    modifiers: [{ name: 'offset', options: { offset: [0, -14] } }],
  },
};

const collapsedTooltipSlotProps = {
  popper: {
    modifiers: [{ name: 'offset', options: { offset: [0, -33] } }],
  },
};

const UserNavigation: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logoutSystem } = useLogout();
  const { id } = useParams();

  const { currentPath, currentLocation } = useAppSelector(selectGlobal);

  const {
    isRideSaved,
    isSOSaved,
    savedRideId,
    savedSOId,
    isShowSO,
    isShowRide,
    rideByIdData,
  } = useAppSelector(selectRides);

  const { isMemberSaved } = useAppSelector(selectMembersInfo);
  const { isPayorSaved } = useAppSelector(selectPayorsInfo);
  const { isContractorSaved } = useAppSelector(selectContractor);
  const { isPlacesSaved } = useAppSelector(selectPlaces);
  const { isContractorVersion, userType, userInfo } =
    useAppSelector(selectUsers);
  const { contractorInfo } = useAppSelector(selectContractor);
  const { data: canCreateRideStatus } = useGetCanCreateRideStatusQuery(
    undefined,
    {
      skip: currentPath !== 'reporting',
      refetchOnMountOrArgChange: true,
    }
  );

  const homeNavigationData = useMemo(
    () =>
      getHomeNavigationData(
        userType,
        contractorInfo?.name || 'Contractor',
        userInfo?.permissions
      ),
    [contractorInfo?.name, userType, userInfo?.permissions]
  );

  const {
    adminNavigationData,
    assignmentNavigationData,
    contractorsNavigationData,
    membersNavigationData,
    payorsNavigationData,
    placesNavigationData,
    ridesNavigationData,
    monitoringNavigationData,
    standingOrderNavigationData,
    reportsNavigationData,
    profileNavigationData,
  } = data;

  const [currentNavigationData, setCurrentNavigationData] = useState<
    Array<navigationItemType>
  >([]);
  const [expandedNavigationId, setExpandedNavigationId] =
    useState<navigationItemType['id']>(null);
  const [openFlyoutId, setOpenFlyoutId] =
    useState<navigationItemType['id']>(null);

  const [isMobile, setIsMobile] = useState<boolean>(
    () => window.innerWidth <= 1100
  );

  const [isBurgerOpen, setIsBurgerOpen] = useState<boolean>(() => {
    const stored = sessionSetter.getItem<boolean>('burgerNavToggler');
    return stored ?? false;
  });

  const closeBurgerNav = () => {
    sessionSetter.setItem('burgerNavToggler', false);
    window.dispatchEvent(new Event('burgerNavTogglerChange'));
  };

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    const stored = sessionSetter.getItem<boolean>('sidebarCollapsed');
    return stored ?? false;
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 1100);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const handleBurgerToggle = () => {
      const stored = sessionSetter.getItem<boolean>('burgerNavToggler');
      setIsBurgerOpen(stored ?? false);
    };

    handleBurgerToggle();
    window.addEventListener('burgerNavTogglerChange', handleBurgerToggle);
    return () =>
      window.removeEventListener('burgerNavTogglerChange', handleBurgerToggle);
  }, []);

  useEffect(() => {
    const handleCollapsedToggle = () => {
      const stored = sessionSetter.getItem<boolean>('sidebarCollapsed');
      setIsCollapsed(stored ?? false);
      setOpenFlyoutId(null);
    };

    handleCollapsedToggle();
    window.addEventListener('sidebarCollapsedChange', handleCollapsedToggle);
    return () =>
      window.removeEventListener(
        'sidebarCollapsedChange',
        handleCollapsedToggle
      );
  }, []);

  useEffect(() => {
    if (userType === CONTRACTOR_USER) {
      setCurrentNavigationData(homeNavigationData);
      tabNameSetter.setTabName((homeNavigationData[0].name ?? '') as string);
      return;
    }

    switch (currentPath) {
      case 'home':
        setCurrentNavigationData(homeNavigationData);
        tabNameSetter.setTabName(
          (membersNavigationData[0].name ?? '') as string
        );
        break;
      case 'member':
        const updatedMembersNavigation = !isContractorVersion
          ? membersNavigationData.filter((item) => item.title !== 'Preferences')
          : membersNavigationData;
        setCurrentNavigationData(
          isMemberSaved
            ? updatedMembersNavigation.map((item) => {
                if (item.title == 'Information') {
                  return {
                    ...item,
                    href: `${item.href}`,
                  };
                } else {
                  return { ...item };
                }
              })
            : updatedMembersNavigation.slice(0, 1)
        );
        tabNameSetter.setTabName(
          (membersNavigationData[0].name ?? '') as string
        );
        break;
      case 'payor':
        setCurrentNavigationData(
          isPayorSaved
            ? payorsNavigationData.map((item) => {
                if (item.title == 'Information') {
                  return {
                    ...item,
                    href: `${item.href}`,
                  };
                } else {
                  return { ...item };
                }
              })
            : payorsNavigationData.slice(0, 1)
        );
        tabNameSetter.setTabName(
          (payorsNavigationData[0].name ?? '') as string
        );
        break;
      case 'user':
        setCurrentNavigationData(adminNavigationData);
        tabNameSetter.setTabName((adminNavigationData[0].name ?? '') as string);
        break;
      case 'place':
        setCurrentNavigationData(
          isPlacesSaved
            ? placesNavigationData
            : placesNavigationData.slice(0, 1)
        );
        tabNameSetter.setTabName(
          (placesNavigationData[0].name ?? '') as string
        );
        break;
      case 'ride':
        setCurrentNavigationData(
          isRideSaved
            ? ridesNavigationData
                .filter((el) =>
                  isShowSO || rideByIdData?.standingOrder
                    ? el
                    : el.title !== TAB_NAME_TITLES.STANDING_ORDER_DETAILS
                )
                .map((el) => {
                  if (el.title !== TAB_NAME_TITLES.STANDING_ORDER_DETAILS) {
                    return {
                      ...el,
                      href: `${el.href}`,
                    };
                  }
                  return {
                    ...el,
                    href: `${el.href}/details`,
                  };
                })
            : ridesNavigationData.slice(0, 1)
        );
        tabNameSetter.setTabName((ridesNavigationData[0].name ?? '') as string);
        break;
      case 'monitoring':
        setCurrentNavigationData(monitoringNavigationData);
        tabNameSetter.setTabName(
          (monitoringNavigationData[0].name ?? '') as string
        );
        break;
      case 'assignment':
        setCurrentNavigationData(assignmentNavigationData);
        tabNameSetter.setTabName(
          (assignmentNavigationData[0].name ?? '') as string
        );
        break;
      case 'contractor':
        setCurrentNavigationData(
          isContractorSaved
            ? contractorsNavigationData
                .filter(
                  (item) =>
                    !(
                      item.title === TAB_NAME_TITLES.OPERATIONS &&
                      isContractorVersion === false
                    )
                )
                .map((item) => {
                  if (item.title == TAB_NAME_TITLES.INFORMATION) {
                    return {
                      ...item,
                      href: `${item.href}`,
                    };
                  } else {
                    return { ...item };
                  }
                })
            : contractorsNavigationData.slice(0, 1)
        );
        tabNameSetter.setTabName(
          (contractorsNavigationData[0].name ?? '') as string
        );
        break;
      case 'standingOrder':
        setCurrentNavigationData(
          isSOSaved
            ? standingOrderNavigationData.map((el) => {
                return {
                  ...el,
                  href: `${el.href}`,
                };
              })
            : standingOrderNavigationData.slice(0, 1)
        );
        tabNameSetter.setTabName(
          (standingOrderNavigationData[0].name ?? '') as string
        );
        break;
      case 'reporting':
        const hiddenReportingTabs = [
          ...(!isContractorVersion ? ['/rideshare-reports'] : []),
          ...(!canCreateRideStatus ? ['/contractor-rejected-reports'] : []),
        ];

        setCurrentNavigationData(
          reportsNavigationData
            .filter((item) => !hiddenReportingTabs.includes(item.href))
            .map((item) =>
              item.href === '/rideshare-reports'
                ? {
                    ...item,
                    title: isContractorVersion
                      ? 'Contractor Reports'
                      : TAB_NAME_TITLES.RIDESHARE_REPORTS,
                  }
                : item
            )
        );
        tabNameSetter.setTabName(
          (reportsNavigationData[0].name ?? '') as string
        );
        break;
      case 'profile':
        setCurrentNavigationData(profileNavigationData);
        tabNameSetter.setTabName(
          (profileNavigationData[0].name ?? '') as string
        );
        break;
      default:
        setCurrentNavigationData([]);
        tabNameSetter.setTabName('' as string);
        break;
    }
  }, [
    currentPath,
    isRideSaved,
    savedRideId,
    isSOSaved,
    savedSOId,
    isMemberSaved,
    isPayorSaved,
    isContractorVersion,
    canCreateRideStatus,
    isContractorSaved,
    isPlacesSaved,
    isShowSO,
    isShowRide,
    rideByIdData,
    userType,
    homeNavigationData,
  ]);

  useEffect(() => {
    const activeGroup = currentNavigationData.find((item) =>
      item.children?.some(
        (child) =>
          location.pathname === child.href ||
          location.pathname.startsWith(`${child.href}/`)
      )
    );

    setExpandedNavigationId(activeGroup?.id ?? null);
  }, [currentNavigationData, location.pathname]);

  const encryptRequired = [
    'member',
    'payor',
    'ride',
    'contractor',
    'standingOrder',
  ];

  const shouldEncrypt = encryptRequired.includes(currentPath);

  const getLinkTo = (item: navigationItemType) => {
    if (userType === CONTRACTOR_USER) return item.href;

    const isHome = currentPath === 'home' && userType !== CONTRACTOR_USER;

    return isHome
      ? '/'
      : currentLocation && item.title !== TAB_NAME_TITLES.STANDING_ORDER_DETAILS
        ? `/${currentLocation}${item.href}`
        : item.href;
  };

  const navigateToItem = (item: navigationItemType) => {
    closeBurgerNav();
    tabNameSetter.setTabName(item.name ?? '');

    const linkTo = getLinkTo(item);
    const currentXHeader =
      sessionSetter.getItem<Record<string, unknown>>(X_HEADER_ID_NAME) ?? {};
    const shouldEncryptItem = userType !== CONTRACTOR_USER && shouldEncrypt;

    const finalLink = shouldEncryptItem
      ? `${linkTo}/${encrypt(currentXHeader)}`
      : linkTo;

    navigate(finalLink, {
      state: {
        xHeader: id ?? location.state?.xHeader,
      },
    });
  };

  const renderFlyoutMenu = (item: navigationItemType) => (
    <div className="navigation-flyout">
      <span className="navigation-flyout-title">{item.title}</span>
      <ul className="navigation-flyout-list">
        {item.children?.map((child) => {
          const childLinkTo = getLinkTo(child);
          const isChildActive =
            location.pathname === childLinkTo ||
            location.pathname.startsWith(`${childLinkTo}/`);

          return (
            <li
              data-test-id={`${child.title.replace(/\s+/g, '-').toLowerCase()}-c-user-navigation-flyout`}
              key={`flyout-${item.id}-${child.id}`}
            >
              <a
                className={isChildActive ? 'active' : ''}
                onClick={() => {
                  setOpenFlyoutId(null);
                  setExpandedNavigationId(item.id);
                  navigateToItem(child);
                }}
              >
                {child.Icon}
                <h4>{child.title}</h4>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );

  const renderNavigationItem = (
    item: navigationItemType,
    keyPrefix = 'root'
  ) => {
    const linkTo = getLinkTo(item);
    const hasChildren = Boolean(item.children?.length);
    const isExpanded = hasChildren && expandedNavigationId === item.id;
    const isExactOrNestedPath =
      location.pathname === linkTo ||
      location.pathname.startsWith(`${linkTo}/`);
    const isChildActive = Boolean(
      item.children?.some((child) => {
        const childLinkTo = getLinkTo(child);

        return (
          location.pathname === childLinkTo ||
          location.pathname.startsWith(`${childLinkTo}/`)
        );
      })
    );
    const isActive =
      (hasChildren && isCollapsed && isChildActive) ||
      (!hasChildren &&
        (isExactOrNestedPath ||
          (location.pathname.includes(item.href.split('/')?.[1]) &&
            userType !== CONTRACTOR_USER)));

    const hasFlyout = isCollapsed && hasChildren;
    const navigationLink = (
      <a
        className={`${isActive ? 'active' : ''} ${hasChildren ? 'navigation-parent' : ''} ${isExpanded ? 'expanded' : ''}`}
        aria-expanded={hasChildren ? isExpanded : undefined}
        onClick={() => {
          if (hasChildren) {
            setExpandedNavigationId(item.id);
          } else if (keyPrefix === 'root') {
            setExpandedNavigationId(null);
          }
          setOpenFlyoutId(null);
          navigateToItem(item.children?.[0] ?? item);
        }}
      >
        {item.Icon}
        <h4>
          <TruncatedText text={item.title} />
        </h4>
      </a>
    );

    return (
      <li
        className={hasChildren ? 'navigation-group' : undefined}
        data-test-id={`${item.title.replace(/\s+/g, '-').toLowerCase()}-c-user-navigation`}
        key={`${keyPrefix}-${item.id}`}
      >
        {hasFlyout ? (
          <NavFlyoutTooltip
            placement="right-start"
            leaveDelay={120}
            open={openFlyoutId === item.id}
            onOpen={() => setOpenFlyoutId(item.id)}
            onClose={() => setOpenFlyoutId(null)}
            title={renderFlyoutMenu(item)}
            slotProps={flyoutSlotProps}
          >
            <span className="navigation-link-wrapper">{navigationLink}</span>
          </NavFlyoutTooltip>
        ) : (
          <InfoTooltip
            title={isCollapsed ? item.title : ''}
            slotProps={isCollapsed ? collapsedTooltipSlotProps : undefined}
            className="navigation-link-wrapper"
          >
            {navigationLink}
          </InfoTooltip>
        )}

        {isExpanded && (
          <ul className="navigation-children">
            {item.children?.map((child) =>
              renderNavigationItem(child, `${keyPrefix}-${item.id}`)
            )}
          </ul>
        )}
      </li>
    );
  };

  return (
    <>
      <div
        data-test-id="c-user-navigation"
        className={`overlayBlock ${isMobile && isBurgerOpen ? 'overlayBlock-open' : ''}`}
        onClick={() => {
          if (isMobile) closeBurgerNav();
        }}
      />

      <div
        className={`user-navigation
    ${isMobile && isBurgerOpen ? 'burger-open' : ''}
    ${!isMobile && isCollapsed ? 'collapsed' : ''}
  `}
      >
        <div>
          <Link to="/" className="logo" onClick={closeBurgerNav}>
            <LogoIcon />
          </Link>
          <Link to="/" className="small_logo">
            <SmallLogoIcon />
          </Link>
          <ul>
            {currentNavigationData.map((item) => renderNavigationItem(item))}
          </ul>
        </div>

        <InfoTooltip
          title={isCollapsed ? 'Log out' : ''}
          slotProps={isCollapsed ? collapsedTooltipSlotProps : undefined}
          disableChildrenWrapper
        >
          <div
            className="logout"
            onClick={() => {
              closeBurgerNav();
              AlertModal.show({
                title: 'Are you sure you want to logout?',
                message:
                  'Logging out may discard unsaved data. Please take a moment to save your work if needed. Do you want to continue?',
                buttons: [
                  {
                    title: 'Cancel',
                    color: '#C6470FFF',
                    onClick: () => {},
                  },
                  {
                    title: 'Continue',
                    color: '#4F7EFFFF',
                    onClick: logoutSystem,
                  },
                ],
              });
            }}
          >
            <LogOutIcon />
            <h4>Logout</h4>
          </div>
        </InfoTooltip>
      </div>
    </>
  );
};

export default UserNavigation;
